import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check, Loader2, Layers, Lock } from 'lucide-react';
import { useMe } from '../hooks/useMe.js';
import { useSwitchEntity } from '../hooks/useSwitchEntity.js';

const GROUP_SLUG = 'group';
const GROUP_LABEL = 'Vue groupe';

// A membership is a "private entity" once it has an owner and nobody else
// has been granted access yet — historical entities (owner_user_id null)
// are never flagged private.
function isPrivateMembership(m) {
  return m.owner_user_id != null && m.member_count === 1;
}

export default function EntitySelector() {
  const { data: me, isLoading } = useMe();
  const switchMut = useSwitchEntity();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    function onClickOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  if (isLoading || !me) {
    return (
      <div className="px-4 py-5 border-b border-slate-100">
        <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Treasury</p>
        <p className="text-sm text-slate-400">Chargement…</p>
      </div>
    );
  }

  const memberships = me.memberships ?? [];
  const isGroup = me.scope === GROUP_SLUG;
  const activeMembership = memberships.find((m) => m.entity_id === me.active_entity_id);
  const canSwitch = memberships.length > 1;
  const groupAvailable = memberships.filter((m) => m.include_in_group).length >= 2;

  const displayName = isGroup ? GROUP_LABEL : (activeMembership?.name ?? 'Aucune entité');
  const activeIsPrivate = !isGroup && activeMembership && isPrivateMembership(activeMembership);

  function handleSelect(slug) {
    // Same-entity click is a no-op.
    if (!isGroup && slug === activeMembership?.slug) {
      setOpen(false);
      return;
    }
    if (isGroup && slug === GROUP_SLUG) {
      setOpen(false);
      return;
    }
    switchMut.mutate(slug, {
      onSettled: () => setOpen(false),
    });
  }

  return (
    <div className="relative border-b border-slate-100" ref={wrapperRef}>
      <button
        type="button"
        onClick={() => canSwitch && setOpen((v) => !v)}
        disabled={!canSwitch || switchMut.isPending}
        className={`w-full px-4 py-5 flex items-center justify-between text-left ${
          canSwitch ? 'hover:bg-slate-50' : 'cursor-default'
        }`}
        aria-haspopup={canSwitch ? 'listbox' : undefined}
        aria-expanded={canSwitch ? open : undefined}
      >
        <div className="min-w-0 flex-1">
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Treasury</p>
          <p className="text-sm font-semibold text-slate-800 truncate flex items-center gap-2">
            {isGroup && <Layers size={14} className="text-slate-500 shrink-0" aria-hidden />}
            <span className="truncate">{displayName}</span>
            {activeIsPrivate && (
              <Lock size={12} className="text-slate-400 shrink-0" aria-label="Société privée" role="img" />
            )}
          </p>
        </div>
        {switchMut.isPending ? (
          <Loader2 size={16} className="text-slate-400 animate-spin shrink-0 ml-2" aria-hidden />
        ) : canSwitch ? (
          <ChevronDown
            size={16}
            className={`text-slate-400 shrink-0 ml-2 transition-transform ${open ? 'rotate-180' : ''}`}
            aria-hidden
          />
        ) : null}
      </button>

      {open && canSwitch && (
        <ul
          className="absolute z-20 left-2 right-2 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg py-1 max-h-72 overflow-y-auto"
          role="listbox"
        >
          {memberships.map((m) => {
            const isActive = !isGroup && m.entity_id === activeMembership?.entity_id;
            return (
              <li key={m.entity_id}>
                <button
                  type="button"
                  onClick={() => handleSelect(m.slug)}
                  className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-left ${
                    isActive
                      ? 'bg-slate-100 text-slate-900 font-medium'
                      : 'text-slate-700 hover:bg-slate-50'
                  }`}
                  role="option"
                  aria-selected={isActive}
                >
                  <span className="flex min-w-0 items-center gap-1.5 truncate">
                    <span className="truncate">{m.name}</span>
                    {isPrivateMembership(m) && (
                      <Lock
                        size={12}
                        className="text-slate-400 shrink-0"
                        aria-label="Société privée"
                        role="img"
                      />
                    )}
                  </span>
                  {isActive && <Check size={14} className="text-slate-500 shrink-0" aria-hidden />}
                </button>
              </li>
            );
          })}

          {groupAvailable && (
            <>
              <li className="border-t border-slate-100 my-1" aria-hidden />
              <li>
                <button
                  type="button"
                  onClick={() => handleSelect(GROUP_SLUG)}
                  className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-left ${
                    isGroup
                      ? 'bg-slate-100 text-slate-900 font-medium'
                      : 'text-slate-700 hover:bg-slate-50'
                  }`}
                  role="option"
                  aria-selected={isGroup}
                >
                  <span className="flex items-center gap-2 truncate">
                    <Layers size={14} className="shrink-0 text-slate-500" aria-hidden />
                    {GROUP_LABEL}
                  </span>
                  {isGroup && <Check size={14} className="text-slate-500 shrink-0" aria-hidden />}
                </button>
              </li>
            </>
          )}
        </ul>
      )}
    </div>
  );
}
